import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import Placeholder from '@tiptap/extension-placeholder';
import Toolbar from './Toolbar';

type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
};

export default function MarkdownEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  autoFocus = false,
}: MarkdownEditorProps) {
  const editor = useEditor({
    autofocus: autoFocus ? 'end' : false,
    extensions: [
      StarterKit.configure({
        link: { openOnClick: false },
      }),
      Markdown,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: value,
    contentType: 'markdown',
    onUpdate: ({ editor }) => {
      // Markdown extension is registered above, so editor.markdown is defined here.
      const markdown = editor.markdown!.serialize(editor.getJSON());
      onChange(markdown);
    },
    onBlur: () => {
      onBlur?.();
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none caret-foreground caret-foreground placeholder:text-muted-foreground aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex-1 min-h-16 w-full px-3 py-2 text-base outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
      },
      handleDOMEvents: {
        click: (_view, event) => {
          const target = event.target as HTMLElement;
          const anchor = target.closest('a');
          if (!anchor) return false;

          if (event.shiftKey || event.metaKey || event.ctrlKey) {
            event.preventDefault();
            window.open(anchor.href, '_blank', 'noopener,noreferrer');
            return true;
          }

          return false;
        },
      },
    },
  });

  if (!editor) return null;

  return (
    <div
      className={`flex flex-col rounded-md border border-input bg-transparent shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] dark:bg-input/30 ${className ?? ''}`}
    >
      <Toolbar editor={editor} />
      <EditorContent
        editor={editor}
        className={'flex flex-1 flex-col min-h-0 overflow-auto'}
      />
    </div>
  );
}
